import _ from 'lodash';

import type { Many, ReadonlyTable } from '@adflow/shared/util-js';
import { many } from '@adflow/shared/util-js';

import type { PermissionKey } from './permission-types';

export type PermissionMetadata<P extends string, A extends string> =
	| 'unused'
	| {
			/**
			 * A list of permissions that are needed for this permission to be useful.
			 */
			readonly needs: readonly PermissionKey<P, A>[];

			/**
			 * A list of permissions that need this permission to be useful.
			 *
			 * This is the inverse of needs.
			 */
			readonly neededBy: readonly PermissionKey<P, A>[];
	  };

export type PermissionsMetadata<
	P extends string,
	A extends string,
> = ReadonlyTable<P, A, PermissionMetadata<P, A>>;

export type PermissionMetadataDef<P extends string, A extends string> =
	| 'unused'
	| {
			needs?: Many<[P, A] | A>;
	  };

export type PermissionsMetadataDef<
	P extends string,
	A extends string,
> = ReadonlyTable<P, A, PermissionMetadataDef<P, A>>;

/**
 * Convert a short hand PermissionsMetadataDef into a complete PermissionsMetadata.
 *
 * PermissionsMetadataDef is easier to write by hand,
 * but PermissionsMetadata is easier to process and use.
 */
// TODO Include transitive edges.
export function makePermissionMetadata<P extends string, A extends string>(
	permissionTypes: readonly P[],
	actionTypes: readonly A[],
	x: PermissionsMetadataDef<P, A>,
	superPerms: readonly PermissionKey<P, A>[]
): {
	permissionsMetadata: PermissionsMetadata<P, A>;
	superPermDeps: readonly PermissionKey<P, A>[];
} {
	type Node = PermissionKey<P, A>;
	type NeedEdge = readonly [neededBy: Node, needs: Node];

	const nodes: Node[] = [];
	const edges: NeedEdge[] = [];

	// Use the PermissionsMetadataDef to construct a directed graph of the permission's dependencies.
	for (const p of permissionTypes) {
		for (const a of actionTypes) {
			const m: PermissionMetadataDef<P, A> = x?.[p]?.[a] ?? {};

			if (m === 'unused') {
				continue;
			}

			nodes.push([p, a] as const);

			for (const n of many(m.needs)) {
				const nn = typeof n === 'string' ? ([p, n] as const) : n;
				edges.push([[p, a] as const, nn]);
			}
		}
	}

	// Use the graph to construct PermissionsMetadata.
	const metadata = {} as PermissionsMetadata<P, A>;
	for (const p of permissionTypes) {
		for (const a of actionTypes) {
			if (!_.some(nodes, (n) => _.isEqual(n, [p, a]))) {
				_.set(metadata, [p, a], 'unused');
				continue;
			}

			const neededBy: Node[] = [];
			for (const [neededBy_] of _.filter(edges, ([, needs]) =>
				_.isEqual(needs, [p, a])
			)) {
				neededBy.push(neededBy_);
			}

			const needs: Node[] = [];
			for (const [, needs_] of _.filter(edges, ([neededBy_]) =>
				_.isEqual(neededBy_, [p, a])
			)) {
				needs.push(needs_);
			}

			_.set(metadata, [p, a], { needs, neededBy });
		}
	}

	// Create permissionSystem.superPermDeps
	const toCheckPerms = [...superPerms];
	const superPermDeps: PermissionKey<P, A>[] = [];

	let toCheckPerm = toCheckPerms.pop();
	while (toCheckPerm) {
		const alreadyChecked = superPermDeps.some((dep) =>
			_.isEqual(dep, toCheckPerm)
		);
		if (!alreadyChecked) {
			superPermDeps.push(toCheckPerm);
		}

		const [P, A] = toCheckPerm;

		const permMetadata = metadata[P][A];
		if (permMetadata !== 'unused') {
			for (const need of permMetadata.needs) {
				const needAlreadyChecked = superPermDeps.some((dep) =>
					_.isEqual(dep, need)
				);
				if (!needAlreadyChecked) {
					toCheckPerms.push(need);
				}
			}
		}

		toCheckPerm = toCheckPerms.pop();
	}

	return {
		permissionsMetadata: metadata,
		superPermDeps,
	};
}
